import { createContext, useContext, useEffect, useMemo, useState, useCallback, ReactNode } from "react";
import { LANGUAGES, LANGUAGE_BY_CODE, LanguageCode, RTL_LANGUAGES } from "@/lib/languages";
import { setupDomTranslator, setDomTranslatorLanguage, translateString } from "@/lib/domTranslator";


interface LanguageContextValue {
  language: LanguageCode;
  setLanguage: (code: LanguageCode) => void;
  t: (text: string) => string;
  isTranslating: boolean;
  languages: typeof LANGUAGES;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

const STORAGE_LANG = "ss_lang";

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const stored = (typeof window !== "undefined" && localStorage.getItem(STORAGE_LANG)) as LanguageCode | null;
    if (stored && LANGUAGE_BY_CODE[stored]) return stored;
    return "en";
  });

  const setLanguage = useCallback((code: LanguageCode) => {
    setLanguageState(code);
    try { localStorage.setItem(STORAGE_LANG, code); } catch {}
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute("lang", language);
    html.setAttribute("dir", RTL_LANGUAGES.includes(language) ? "rtl" : "ltr");
    setDomTranslatorLanguage(language);
  }, [language]);

  useEffect(() => {
    const teardown = setupDomTranslator();
    setDomTranslatorLanguage(language);
    return teardown;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = useCallback((text: string) => {
    if (!text || language === "en") return text;
    return translateString(language, text);
  }, [language]);


  const value = useMemo<LanguageContextValue>(() => ({
    language, setLanguage, t, isTranslating: false, languages: LANGUAGES,
  }), [language, setLanguage, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    return {
      language: "en",
      setLanguage: () => {},
      t: (s) => s,
      isTranslating: false,
      languages: LANGUAGES,
    };
  }
  return ctx;
}

export function T({ children }: { children: string }) {
  const { t } = useLanguage();
  return <>{t(children)}</>;
}
